import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import styles from './UserManual.module.css'

const SECTIONS = [
  {
    id: 'copyright',
    title: 'Video content and copyright ownership',
    paragraphs: [
      'The Judo Kata Practice Tool (the Tool) uses YouTube embeds for video playback. It does not offer video downloading or host copies of YouTube video files. Video availability and playback remain subject to YouTube and the uploader’s settings.',
      'Rights in third-party video and audio content remain with their respective rights holders. The Tool does not claim ownership of that content. Linking to or embedding a video does not transfer copyright or grant permission to reuse it elsewhere.',
      'If you believe a public reference in the Tool infringes your rights, contact developers@the-fortitudes.com with the relevant Tool page, video URL, and details of your claim. For removal of the video from YouTube itself, use YouTube’s reporting process or contact the uploader.',
    ],
  },
  {
    id: 'platform',
    title: 'Independent tool and YouTube services',
    paragraphs: [
      'The Tool is an independent educational and training companion. It is not affiliated with, endorsed by, or sponsored by Google LLC, YouTube, the International Judo Federation (IJF), Kodokan, or any other official Judo governing body.',
      'YouTube playback is provided through its embedded player and remains subject to YouTube’s Terms of Service and applicable platform restrictions. Videos may become unavailable, embedding may be disabled, and playback may include advertisements. The Tool does not provide a right to bypass those restrictions.',
    ],
  },
  {
    id: 'cloud-sync',
    title: 'Free local features and planned Cloud Sync',
    paragraphs: [
      'The current local-first features let you use YouTube references and save your own notes, timestamps, and bookmarks on your device. The Cloud Sync waitlist is an expression of interest, not an active subscription or purchase.',
      'Any future paid Cloud Sync service is intended to cover synchronization, cross-device backup, and management of user-created data—not access to YouTube videos. YouTube content is not sold by the Tool. Planned benefits and pricing are summarized on the Cloud Sync page (/cloud-sync). Final service limits and subscription terms will be confirmed before purchase.',
    ],
  },
  {
    id: 'privacy',
    title: 'Local data, third-party services, and privacy',
    paragraphs: [
      'Local practice data is stored in your browser on the device you use. Clearing site data, changing browsers or devices, or browser storage eviction can remove that data. Keep exports of information you want to preserve; local storage is not a guaranteed backup.',
      'Loading YouTube embeds connects your browser to third-party services. Those services may process information such as your IP address, device information, and cookies under their own policies. Local-first storage does not mean that no network data is processed by YouTube or the website’s hosting provider.',
      'Joining the waitlist or sending feedback by email shares your email address and message with the development team and the email services involved. Do not include sensitive personal information. Contact us with privacy questions or requests concerning information you have sent us.',
      'If Cloud Sync launches, its data handling, retention, deletion, and service-provider details will be explained in a separate privacy notice before you enable it. This disclaimer is not a complete privacy policy for a future cloud service.',
    ],
  },
  {
    id: 'safety',
    title: 'Training safety and limitations',
    paragraphs: [
      'Videos, technical notes, and timestamps are supplementary study aids, not a substitute for instruction from a qualified Judo coach. Judo and martial arts training involve risks of serious injury. Practice in a suitable training environment with appropriate mats, qualified supervision, and a suitable training partner.',
      'The Tool and its references may contain errors or become unavailable. No guarantee is made about accuracy, suitability for your circumstances, continuous availability, or preservation of locally stored data. To the extent permitted by applicable law, the developers disclaim liability for loss or injury arising from use of the Tool. Nothing in this notice excludes rights or liabilities that cannot lawfully be excluded.',
    ],
  },
]

export function Disclaimer() {
  useEffect(() => {
    const previousTitle = document.title
    document.title = 'Legal Disclaimer | Judo Kata Practice Tool'
    window.scrollTo(0, 0)
    return () => { document.title = previousTitle }
  }, [])

  return (
    <article className={styles.page} lang="en">
      <header className={styles.hero}>
        <div className={styles.heroAtmosphere} aria-hidden="true">
          <span className={styles.washA} />
          <span className={styles.washB} />
          <span className={styles.washC} />
          <span className={styles.silk} />
        </div>
        <div className={styles.heroInner}>
          <Link to="/" className={styles.backLink}>← Back to kata library</Link>
          <p className={styles.eyebrow}>Judo Kata Practice Tool</p>
          <h1 className={styles.title}>Legal Disclaimer</h1>
          <p className={styles.subtitle}>Last updated: October 29, 2026</p>
        </div>
      </header>
      <div className={styles.body}>
        <p className={styles.sectionLead}>Please read this notice before using the Tool. It explains third-party video references, planned paid services, local data, and training risks.</p>
        <nav className={styles.toc} aria-labelledby="disclaimer-contents">
          <h2 id="disclaimer-contents" className={styles.tocTitle}>On this page</h2>
          <ol className={styles.tocList}>
            {SECTIONS.map((section) => (
              <li key={section.id}><a href={`#${section.id}`}>{section.title}</a></li>
            ))}
            <li><a href="#contact">Contact and platform policies</a></li>
          </ol>
        </nav>
        <div className={styles.sections}>
          {SECTIONS.map((section) => (
            <section key={section.id} id={section.id} className={styles.section} aria-labelledby={`${section.id}-heading`}>
              <h2 id={`${section.id}-heading`} className={styles.sectionTitle}>{section.title}</h2>
              {section.paragraphs.map((paragraph) => <p key={paragraph} className={styles.sectionLead}>{paragraph}</p>)}
              {section.id === 'cloud-sync' ? (
                <p className={styles.sectionLead}>
                  <Link to="/cloud-sync">Cloud Sync benefits and planned pricing</Link>
                </p>
              ) : null}
            </section>
          ))}
          <section id="contact" className={styles.section} aria-labelledby="contact-heading">
            <h2 id="contact-heading" className={styles.sectionTitle}>Contact and platform policies</h2>
            <p className={styles.sectionLead}>For copyright concerns, legal inquiries, feedback, or privacy requests, email <a href="mailto:developers@the-fortitudes.com">developers@the-fortitudes.com</a>.</p>
            <ul className={styles.steps}>
              <li><a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">YouTube Terms of Service (opens in a new tab)</a></li>
              <li><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy (opens in a new tab)</a></li>
            </ul>
          </section>
        </div>
        <nav className={styles.footerLinks} aria-label="Related pages">
          <Link to="/">Back to kata library</Link>
          <Link to="/cloud-sync">Cloud Sync</Link>
          <Link to="/data">Manage local data</Link>
        </nav>
      </div>
    </article>
  )
}
